/**
 * MeshStandard のマップサンプリングを流用し、ライティングせず MRT に書く。
 * RT0: albedo.rgb + metalness、RT1: worldNormal (0–1) + roughness、plus depth。
 */

import * as THREE from 'three';

export const LAYER_LIT = 0;
export const LAYER_OVERLAY = 1;
export const LAYER_TRANSPARENT = 2;
/** 照準・範囲枠などの線。ニアクリップを別 near で描く。 */
export const LAYER_OVERLAY_LINES = 3;

const GBUFFER_MARK = 'GBUFFER_MRT';

export function setObjectLayer(object: THREE.Object3D, layer: number): void {
  object.traverse((child) => child.layers.set(layer));
}

export class GBuffer {
  readonly target: THREE.WebGLRenderTarget;
  readonly passUniform = { value: 1 };
  private readonly size = new THREE.Vector2();

  constructor() {
    const depthTexture = new THREE.DepthTexture(1, 1, THREE.FloatType);
    depthTexture.minFilter = THREE.NearestFilter;
    depthTexture.magFilter = THREE.NearestFilter;
    this.target = new THREE.WebGLRenderTarget(1, 1, {
      count: 2,
      type: THREE.HalfFloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: true,
      depthTexture,
    });
    for (const texture of this.target.textures) {
      texture.colorSpace = THREE.NoColorSpace;
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
    }
  }

  get albedoMetal(): THREE.Texture {
    return this.target.textures[0];
  }

  get normalRough(): THREE.Texture {
    return this.target.textures[1];
  }

  get depth(): THREE.DepthTexture {
    return this.target.depthTexture!;
  }

  setupMaterial(material: THREE.Material): void {
    if (material.userData.gbufferMrt) return;
    material.userData.gbufferMrt = true;
    material.toneMapped = false;
    (material as THREE.ShaderMaterial).glslVersion = THREE.GLSL3;
    const previous = material.onBeforeCompile;
    material.onBeforeCompile = (shader, renderer) => {
      previous.call(material, shader, renderer);
      if (shader.fragmentShader.includes(GBUFFER_MARK)) return;
      shader.uniforms.gbufferPass = this.passUniform;
      shader.fragmentShader = `layout(location = 0) out highp vec4 pc_fragColor;
#define gl_FragColor pc_fragColor
layout(location = 1) out highp vec4 gNormal;
uniform float gbufferPass;
${shader.fragmentShader}`
        .replace('#include <lights_fragment_begin>', '')
        .replace('#include <lights_fragment_maps>', '')
        .replace('#include <lights_fragment_end>', '')
        .replace(
          '#include <opaque_fragment>',
          `// ${GBUFFER_MARK}
vec3 worldNormal = normalize( inverseTransformDirection( normal, viewMatrix ) );
if ( gbufferPass > 0.5 ) {
	float emitAmt = max( max( totalEmissiveRadiance.r, totalEmissiveRadiance.g ), totalEmissiveRadiance.b );
	if ( emitAmt > 0.01 ) {
		gl_FragColor = vec4( totalEmissiveRadiance, -1.0 );
	} else {
		gl_FragColor = vec4( diffuseColor.rgb, metalnessFactor );
	}
	gNormal = vec4( worldNormal * 0.5 + 0.5, roughnessFactor );
} else {
	gl_FragColor = vec4( diffuseColor.rgb, diffuseColor.a );
	gNormal = vec4( 0.0 );
}
`
        )
        .replace('#include <tonemapping_fragment>', '')
        .replace('#include <colorspace_fragment>', '')
        .replace('#include <fog_fragment>', '')
        .replace('#include <premultiplied_alpha_fragment>', '')
        .replace('#include <dithering_fragment>', '');
    };
    const previousKey = material.customProgramCacheKey.bind(material);
    material.customProgramCacheKey = () => `${previousKey()}|gbuffer-mrt-glsl3-emit`;
  }

  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    if (this.size.x === w && this.size.y === h) return;
    this.size.set(w, h);
    this.target.setSize(w, h);
  }

  dispose(): void {
    this.target.dispose();
  }
}
